import React from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { InfoCard } from '@eika/card';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { Loader } from '@eika/loader';
import { RadioButtonPanels } from '@eika/radio-button';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import type { Account } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import ActionResultInfo from '../shared/ActionResultInfo/ActionResultInfo';
import { ACCOUNT_NOT_SET } from './constants';

type Props = {
    isLoading: boolean;
    isSuccess: boolean;
    error?: RequestExceptionWithResponse;
    submitOrRetry: () => void;
    accounts: Account[];
    selectedAccountState: [string, React.Dispatch<React.SetStateAction<string>>];
};

const EFakturaAccountModalBody: React.FC<Props> = ({
    isLoading,
    isSuccess,
    accounts,
    submitOrRetry,
    selectedAccountState,
    error,
}) => {
    const translated = useTranslation();
    const [selectedAccount, setSelectedAccount] = selectedAccountState;
    const options = [
        ...(accounts || []).map((account) => {
            return {
                value: account.accountNumber.value,
                label: account.alias,
                description: formatAccountNumber(account.accountNumber),
            };
        }),
        {
            value: ACCOUNT_NOT_SET,
            label: translated.eFakturaAccount.notSetTitle,
            description: translated.eFakturaAccount.notSetDescription,
        },
    ];

    if (isLoading) {
        return (
            <div className="efaktura-account__result-info">
                <Loader />
            </div>
        );
    }

    if (isSuccess || error) {
        return (
            <div className="efaktura-account__result-info">
                <ActionResultInfo
                    title={
                        selectedAccount !== ACCOUNT_NOT_SET
                            ? translated.eFakturaAccount.success
                            : translated.eFakturaAccount.unselectedSuccess
                    }
                    retry={submitOrRetry}
                    defaultError={translated.eFakturaAccount.error}
                    error={error}
                    icon={<CheckmarkAnimation className="efaktura-account__result-icon" />}
                />
            </div>
        );
    }

    if (accounts.length === 0) {
        return (
            <InfoCard className="sgw-layout-item" skipBackground>
                <RichIcon maxHeight={160} minHeight={160} type={RichIconTypes.HAAND_HEIHEI} />
                <InfoCard.TitleH3>{translated.eFakturaAccount.noAccountsTitle}</InfoCard.TitleH3>
                <p className="typography-paragraph">{translated.eFakturaAccount.noAccountsText1}</p>
                <p className="typography-paragraph">{translated.eFakturaAccount.noAccountsText2}</p>
            </InfoCard>
        );
    }

    return (
        <form className="edit-efaktura-account__form">
            {accounts.length > 0 && (
                <RadioButtonPanels
                    label={translated.eFakturaAccount.title}
                    sublabel={translated.eFakturaAccount.description}
                    options={options}
                    selectedOption={selectedAccount}
                    onChange={setSelectedAccount}
                />
            )}
        </form>
    );
};

export default EFakturaAccountModalBody;
